import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wrench, Trash2, Camera } from 'lucide-react';
import { PART_NAMES, SEVERITY_NAMES } from './constants';

// Part Selector Modal Component
export const PartSelectorModal = ({ isOpen, onClose, onSelectPart, existingParts }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Seleccionar Parte Dañada</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
          {Object.entries(PART_NAMES).map(([key, name]) => (
            <Button
              key={key}
              variant="outline"
              size="sm"
              onClick={() => onSelectPart(key)}
              disabled={existingParts.some(p => p.part_name === key)}
              className={`text-left justify-start ${
                existingParts.some(p => p.part_name === key) 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-purple-50 hover:border-purple-300'
              }`}
              data-testid={`part-option-${key}`}
            >
              {name}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Single Damaged Part Card Component
export const DamagedPartCard = ({ part, index, onUpdate, onRemove }) => {
  return (
    <Card className="p-4 border-slate-200">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Badge className="bg-purple-100 text-purple-700">
            {PART_NAMES[part.part_name] || part.part_name}
          </Badge>
          <Badge className={SEVERITY_NAMES[part.severity]?.color || 'bg-slate-100'}>
            {SEVERITY_NAMES[part.severity]?.label || part.severity}
          </Badge>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Severidad</Label>
          <Select
            value={part.severity}
            onValueChange={(v) => onUpdate(index, 'severity', v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SEVERITY_NAMES).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Costo Estimado (₡)</Label>
          <Input
            type="number"
            value={part.estimated_cost}
            onChange={(e) => onUpdate(index, 'estimated_cost', parseFloat(e.target.value) || 0)}
            className="h-9"
            data-testid={`part-cost-${index}`}
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={part.requires_replacement}
              onChange={(e) => onUpdate(index, 'requires_replacement', e.target.checked)}
              className="w-4 h-4 text-purple-600 rounded"
            />
            <span className="text-sm text-slate-700">Requiere reemplazo</span>
          </label>
        </div>
      </div>
      <div className="mt-2">
        <Label className="text-xs">Descripción del daño</Label>
        <Input
          value={part.description}
          onChange={(e) => onUpdate(index, 'description', e.target.value)}
          placeholder="Ej: Abolladuras múltiples, pintura dañada..."
          className="h-9"
        />
      </div>
      <div className="mt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled
          className="text-slate-400"
        >
          <Camera className="w-4 h-4 mr-1" />
          Agregar Fotos (Próximamente)
        </Button>
      </div>
    </Card>
  );
};

// Damaged Parts List Component
export const DamagedPartsList = ({ 
  damagedParts, 
  onAddPart, 
  onUpdatePart, 
  onRemovePart 
}) => {
  const [showPartSelector, setShowPartSelector] = React.useState(false);

  const handleAddPart = (partName) => {
    onAddPart(partName);
    setShowPartSelector(false);
  };

  return (
    <div className="space-y-4">
      <Button
        type="button"
        variant="outline"
        onClick={() => setShowPartSelector(true)}
        className="w-full border-dashed border-2 border-purple-300 text-purple-600 hover:bg-purple-50"
        data-testid="add-damaged-part-btn"
      >
        <Wrench className="w-4 h-4 mr-2" />
        Agregar Parte Dañada
      </Button>

      <PartSelectorModal
        isOpen={showPartSelector}
        onClose={() => setShowPartSelector(false)}
        onSelectPart={handleAddPart}
        existingParts={damagedParts}
      />

      {damagedParts.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Wrench className="w-12 h-12 mx-auto mb-2 text-slate-300" />
          <p>No hay partes dañadas agregadas</p>
          <p className="text-sm">Haz clic en el botón de arriba para agregar</p>
        </div>
      ) : (
        <div className="space-y-3">
          {damagedParts.map((part, index) => (
            <DamagedPartCard
              key={`${part.part_name}-${index}`}
              part={part}
              index={index}
              onUpdate={onUpdatePart}
              onRemove={onRemovePart}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DamagedPartsList;
